/**
 * Microsoft Advertising Reporting Service v13 — Submit/Poll/Download/Unzip/Parse (Story 19.2).
 *
 * Microsoft's Reporting Service is **asynchronous** — fundamentally different from
 * Google Ads' synchronous searchStream. The lifecycle is:
 *
 *   1. Submit:   POST /GenerateReport/Submit → { ReportRequestId }
 *   2. Poll:     POST /GenerateReport/Poll until Status=Success/Error/Pending
 *   3. Download: GET <ReportDownloadUrl>  (signed URL, 5-min validity)
 *   4. Unzip:    ZIP archive → single CSV
 *   5. Parse:    skip preamble + footer rows → string-typed records
 *
 * Tracking IDs live for 2 days. Download URLs live for 5 minutes; if expired,
 * re-poll for a fresh URL (the report itself is still cached server-side).
 *
 * Per-isolate memory ceiling for `fetchMetrics` is ≈ 30 MB at the 90-day cap;
 * future expansion to longer ranges requires streaming CSV parse — not yet
 * needed (Reviewer B perf concern, see story 19.2 dev notes).
 *
 * Security:
 *   - Never log `ReportDownloadUrl` directly; it carries a SAS-style signature
 *     that, while short-lived (5 min), should not appear in error messages.
 *   - All HTTP calls go through `safeFetch`, which sanitizes tokens and URLs
 *     in error messages via `sanitizeErrorMessage`.
 *
 * @see https://learn.microsoft.com/en-us/advertising/guides/request-download-report?view=bingads-13
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { unzipSingleFileToText } from "./unzip";
import {
  MicrosoftAdsApiCallError,
  parseMicrosoftAdsApiError,
} from "./errors";

// ============================================================================
// Constants
// ============================================================================

export const MICROSOFT_ADS_REPORTING_API_BASE =
  "https://reporting.api.bingads.microsoft.com/Reporting/v13";

/**
 * Poll interval. Per AC #2 — constant 2-second polls, 60 attempts → ~2 minute
 * total wall-clock budget. Earlier drafts grew the interval exponentially per
 * Reviewer B's perf concern, but that pushed total wall-clock to ~10 min and
 * blew past AC #2's "≈2 minutes" cap. We choose AC adherence over micro-perf.
 */
export const POLL_INTERVAL_MS = 2_000;
/** Maximum poll attempts before we give up and surface rate_limited. */
export const MAX_POLL_ATTEMPTS = 60;

/** Per-call timeouts. Reporting submit is light, poll is light, download can be a few MB. */
export const SUBMIT_TIMEOUT_MS = 15_000;
export const POLL_TIMEOUT_MS = 10_000;
export const DOWNLOAD_TIMEOUT_MS = 30_000;

// ============================================================================
// Types
// ============================================================================

/**
 * The 8 report types Story 19.2 wires up. Microsoft exposes ~40 report types
 * total — see `_bmad/bmm/data/integration-metrics/microsoft-ads.md` §"Report
 * Types" for the full catalogue. Future stories add more by extending this
 * union and adding a column-list constant in `report-builders.ts`.
 */
export type MicrosoftAdsReportType =
  | "overview"
  | "campaigns"
  | "adGroups"
  | "keywords"
  | "searchQueries"
  | "shopping"
  | "ageGender"
  | "geo";

export type MicrosoftAdsReportStatus = "Pending" | "Success" | "Error";

/**
 * Map our internal report-type discriminator to the Microsoft API's
 * `ReportRequest.Type` field. Microsoft uses the suffixed form everywhere.
 */
export const REPORT_TYPE_TO_API: Record<MicrosoftAdsReportType, string> = {
  overview: "AccountPerformanceReportRequest",
  campaigns: "CampaignPerformanceReportRequest",
  adGroups: "AdGroupPerformanceReportRequest",
  keywords: "KeywordPerformanceReportRequest",
  searchQueries: "SearchQueryPerformanceReportRequest",
  shopping: "ProductDimensionPerformanceReportRequest",
  ageGender: "AgeGenderAudienceReportRequest",
  geo: "GeographicPerformanceReportRequest",
};

export interface ReportSubmitResponse {
  TrackingId?: string;
  ReportRequestId: string;
}

export interface ReportPollResponse {
  ReportRequestStatus: {
    Status: MicrosoftAdsReportStatus;
    ReportDownloadUrl?: string;
  };
}

// ============================================================================
// Headers
// ============================================================================

interface ReportingHeaders {
  Authorization: string;
  DeveloperToken: string;
  CustomerId: string;
  CustomerAccountId?: string;
  Accept: string;
  "Content-Type": string;
}

function buildReportingHeaders(
  accessToken: string,
  developerToken: string,
  customerId: string,
  accountId?: string
): ReportingHeaders {
  if (!accessToken) {
    throw new Error("accessToken is required for Microsoft Ads Reporting API calls");
  }
  if (!developerToken) {
    throw new Error("developerToken is required for Microsoft Ads Reporting API calls");
  }
  if (!customerId) {
    throw new Error("customerId is required for Microsoft Ads Reporting API calls");
  }
  const headers: ReportingHeaders = {
    Authorization: `Bearer ${accessToken}`,
    DeveloperToken: developerToken,
    CustomerId: customerId,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (accountId) {
    headers.CustomerAccountId = accountId;
  }
  return headers;
}

// ============================================================================
// Submit
// ============================================================================

/**
 * Submit step: enqueues a report-generation job and returns its `ReportRequestId`.
 * The body shape is composed by `report-builders.ts` and passed through verbatim.
 */
export async function submitMicrosoftAdsReport(
  accessToken: string,
  developerToken: string,
  customerId: string,
  accountId: string,
  body: unknown
): Promise<ReportSubmitResponse> {
  const headers = buildReportingHeaders(
    accessToken,
    developerToken,
    customerId,
    accountId
  );

  const result = await safeFetch(
    `${MICROSOFT_ADS_REPORTING_API_BASE}/GenerateReport/Submit`,
    {
      method: "POST",
      headers: headers as unknown as Record<string, string>,
      body: JSON.stringify(body),
    },
    SUBMIT_TIMEOUT_MS
  );

  if (!result.success) {
    throw buildReportingApiError(result.error, "Microsoft Ads SubmitGenerateReport failed");
  }

  let data: ReportSubmitResponse;
  try {
    data = (await result.data.json()) as ReportSubmitResponse;
  } catch {
    throw new Error("Invalid Microsoft Ads SubmitGenerateReport response: not JSON");
  }
  if (!data.ReportRequestId) {
    throw new Error("Invalid Microsoft Ads SubmitGenerateReport response: missing ReportRequestId");
  }
  return data;
}

// ============================================================================
// Poll
// ============================================================================

/**
 * Poll step: single-shot. Returns the current status of the report job.
 * Microsoft's docs say only `CustomerId` is required for Poll (not
 * `CustomerAccountId`); we still pass it as defence-in-depth so a future
 * docs change doesn't break us silently.
 */
export async function pollMicrosoftAdsReport(
  accessToken: string,
  developerToken: string,
  customerId: string,
  reportRequestId: string,
  accountId?: string
): Promise<ReportPollResponse> {
  // Microsoft's docs say only `CustomerId` is required for Poll. The story
  // 19.2 dev notes call for sending `CustomerAccountId` as defence-in-depth,
  // so we accept an optional accountId and propagate it when present.
  const headers = buildReportingHeaders(
    accessToken,
    developerToken,
    customerId,
    accountId
  );

  const result = await safeFetch(
    `${MICROSOFT_ADS_REPORTING_API_BASE}/GenerateReport/Poll`,
    {
      method: "POST",
      headers: headers as unknown as Record<string, string>,
      body: JSON.stringify({ ReportRequestId: reportRequestId }),
    },
    POLL_TIMEOUT_MS
  );

  if (!result.success) {
    throw buildReportingApiError(result.error, "Microsoft Ads PollGenerateReport failed");
  }

  let data: ReportPollResponse;
  try {
    data = (await result.data.json()) as ReportPollResponse;
  } catch {
    throw new Error("Invalid Microsoft Ads PollGenerateReport response: not JSON");
  }
  if (!data.ReportRequestStatus?.Status) {
    throw new Error("Invalid Microsoft Ads PollGenerateReport response: missing Status");
  }
  return data;
}

/**
 * Repeatedly polls until the report is ready or the attempt budget is
 * exhausted. Per AC #2 — constant `POLL_INTERVAL_MS` (2s) wait between
 * attempts, capped at `MAX_POLL_ATTEMPTS = 60`, total ≈2 minute budget. The
 * queue consumer (Story 4.13) has a 15-minute budget, so a hit-the-cap → retry
 * cycle from the queue covers reports that take longer than 2 minutes (rare —
 * typical reports complete in 5-15 seconds).
 *
 * Returns the signed `ReportDownloadUrl` on Success.
 * Throws `MicrosoftAdsApiCallError({ type: "rate_limited", ... })` when
 * the attempt cap is hit (so the queue consumer retries the whole fetch).
 * Throws `MicrosoftAdsApiCallError({ type: "unknown", ... })`
 * when Microsoft returns Status=Error.
 */
export async function pollMicrosoftAdsReportUntilDone(
  accessToken: string,
  developerToken: string,
  customerId: string,
  reportRequestId: string,
  options?: {
    sleep?: (ms: number) => Promise<void>;
    intervalMs?: number;
    maxAttempts?: number;
    accountId?: string;
  }
): Promise<string> {
  const sleep = options?.sleep ?? defaultSleep;
  const interval = options?.intervalMs ?? POLL_INTERVAL_MS;
  const cap = options?.maxAttempts ?? MAX_POLL_ATTEMPTS;
  const accountId = options?.accountId;

  for (let attempt = 0; attempt < cap; attempt++) {
    const poll = await pollMicrosoftAdsReport(
      accessToken,
      developerToken,
      customerId,
      reportRequestId,
      accountId
    );
    const status = poll.ReportRequestStatus.Status;

    if (status === "Success") {
      const url = poll.ReportRequestStatus.ReportDownloadUrl;
      if (!url) {
        throw new Error(
          "Microsoft Ads PollGenerateReport returned Status=Success without a ReportDownloadUrl"
        );
      }
      return url;
    }

    if (status === "Error") {
      throw new MicrosoftAdsApiCallError(
        {
          type: "unknown",
          message: "Microsoft Ads report generation failed (Status=Error)",
          status: null,
          retryable: false,
        },
        "Microsoft Ads PollGenerateReport"
      );
    }

    // Pending — wait the constant interval before the next attempt.
    await sleep(interval);
  }

  throw new MicrosoftAdsApiCallError(
    {
      type: "rate_limited",
      message: `Microsoft Ads report did not complete within ${cap} poll attempts`,
      status: null,
      retryable: true,
    },
    "Microsoft Ads PollGenerateReport"
  );
}

// ============================================================================
// Download
// ============================================================================

/**
 * Download step: GETs the signed URL and returns the raw ZIP bytes.
 *
 * Security: the URL itself carries a 5-min SAS signature. We pass it to
 * `safeFetch`, which redacts query parameters in any error message. We also
 * sanitize any error we throw locally so the URL never appears in logs.
 *
 * If the signed URL expires (5-min SAS), Microsoft returns 403. The adapter's
 * caller should re-poll for a fresh URL when it sees an expired-URL signal.
 */
export async function downloadMicrosoftAdsReportZip(
  reportDownloadUrl: string
): Promise<ArrayBuffer> {
  if (!reportDownloadUrl) {
    throw new Error("Microsoft Ads downloadReport called with empty URL");
  }
  const result = await safeFetch(
    reportDownloadUrl,
    { method: "GET" },
    DOWNLOAD_TIMEOUT_MS
  );

  if (!result.success) {
    throw buildReportingApiError(
      result.error,
      "Microsoft Ads report download failed"
    );
  }

  try {
    return await result.data.arrayBuffer();
  } catch (err) {
    throw new Error(
      sanitizeErrorMessage(
        `Microsoft Ads report download body read failed: ${err instanceof Error ? err.message : String(err)}`
      )
    );
  }
}

// ============================================================================
// Download + Unzip → CSV text
// ============================================================================

/**
 * One-shot: download the ZIP at the signed URL, unzip it, and return the
 * single CSV file's UTF-8 text. This is the ergonomic boundary that the
 * adapter calls — composing it from `downloadMicrosoftAdsReportZip` +
 * `unzipSingleFileToText` keeps each step independently testable.
 */
export async function downloadAndExtractCsv(
  reportDownloadUrl: string
): Promise<string> {
  const zipBytes = await downloadMicrosoftAdsReportZip(reportDownloadUrl);
  return unzipSingleFileToText(zipBytes);
}

// ============================================================================
// CSV Parser
// ============================================================================

/**
 * Parses Microsoft Ads CSV report text into raw string-keyed rows.
 *
 * Format (verified live against the Reporting v13 sandbox, April 2026):
 *   Line 1:  ReportName,...
 *   Line 2:  ReportTimeZone,...
 *   Line 3:  LastCompletedAvailableDate,...
 *   Line 4:  ReportAggregation,...
 *   Line 5:  (blank)
 *   Line 6:  ColumnA,ColumnB,ColumnC,...
 *   Line 7+: data rows
 *   Last:    @Copyright ... (ignored)
 *
 * Returns an array of `Record<string, string>` (raw values from CSV);
 * downstream transformers in `transformers.ts` handle type coercion.
 *
 * Defence-in-depth: if the preamble is missing (e.g., corrupt download), we
 * return `[]` rather than crashing. This matches the existing
 * `extractOAuthErrorDescription` pattern (Story 19.1 review #1 L2).
 */
export function parseMicrosoftAdsCsv(
  csvText: string,
  _reportType?: MicrosoftAdsReportType
): Array<Record<string, string>> {
  if (!csvText) return [];

  const lines = csvText.split(/\r?\n/);
  if (lines.length < 7) {
    // Not enough rows to contain the preamble + a header — likely corrupt.
    return [];
  }

  // Find the first blank line — that marks the boundary between the preamble
  // (4 metadata rows) and the column-header row. Microsoft sometimes omits
  // one of the metadata rows on niche report types, so we scan rather than
  // hardcoding "skip 5".
  let blankIdx = -1;
  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    const line = lines[i];
    if (line == null) continue;
    if (line.trim() === "") {
      blankIdx = i;
      break;
    }
  }
  if (blankIdx === -1) {
    // No blank line in the first 10 rows — preamble shape unrecognised.
    return [];
  }

  const headerLine = lines[blankIdx + 1];
  if (!headerLine) return [];

  const columns = parseCsvLine(headerLine);

  const records: Array<Record<string, string>> = [];
  for (let i = blankIdx + 2; i < lines.length; i++) {
    const line = lines[i];
    if (line == null) continue;
    if (line.trim() === "") continue;
    if (line.startsWith("@Copyright")) break;
    const cells = parseCsvLine(line);
    if (cells.length === 0) continue;
    const row: Record<string, string> = {};
    for (let c = 0; c < columns.length; c++) {
      const key = columns[c];
      if (!key) continue;
      row[key] = cells[c] ?? "";
    }
    records.push(row);
  }

  return records;
}

/**
 * Parses a single CSV line honouring quoted commas and quoted newlines (within
 * a single line — Microsoft does not emit multi-line cells in any report type
 * we use). Doubled quotes (`""`) are treated as escaped quotes per RFC 4180.
 */
function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        // Look for an escaped quote.
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === ",") {
      cells.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells.map((c) => c.trim());
}

// ============================================================================
// Internal helpers
// ============================================================================

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Convert a `safeFetch` error into a typed `MicrosoftAdsApiCallError` so the
 * adapter can route it via `microsoftAdsErrorTypeToAdapterCode`. Mirrors the
 * helper in `api.ts` but lives here to keep the reporting module standalone.
 */
function buildReportingApiError(
  safeFetchError: {
    message: string;
    providerMessage?: string;
    providerStatus?: number;
  },
  prefix: string
): MicrosoftAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON; status-only mapping used below
    }
  }
  const parsed = parseMicrosoftAdsApiError(parsedBody, safeFetchError.providerStatus);
  return new MicrosoftAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    prefix
  );
}
